<div>
  <!-- Loading -->
  @if (loading) {
    <div class="flex items-center justify-center py-12">
      <div class="h-6 w-6 animate-spin rounded-full border-b-2 border-brand-500"></div>
      <span class="ml-3 text-sm text-gray-500 dark:text-gray-400">Cargando línea de tiempo...</span>
    </div>
  }

  <!-- Error -->
  @if (error) {
    <div class="rounded-lg border border-error-200 bg-error-50 p-4 dark:border-error-500/20 dark:bg-error-500/10">
      <p class="text-sm text-error-600 dark:text-error-400">{{ error }}</p>
    </div>
  }

  <!-- Timeline list -->
  @if (!loading && !error) {
    @if (logs.length === 0) {
      <div class="text-center py-10">
        <svg class="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <p class="text-sm text-gray-400 dark:text-gray-500">{{ emptyMessage }}</p>
      </div>
    }

    @if (logs.length > 0) {
      <div class="relative">
        <!-- Vertical line -->
        <div class="absolute left-4 top-0 bottom-0 w-px bg-gray-200 dark:bg-gray-700"></div>

        <div class="space-y-0">
          @for (log of logs; track log.id) {
            <div class="relative flex gap-4 py-3">
              <!-- Dot -->
              <div class="relative z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 border-white dark:border-gray-900"
                [ngClass]="{
                  'bg-green-100 dark:bg-green-500/20': log.operationType === 'CREATE' || log.operationType === 'INSERT',
                  'bg-blue-100 dark:bg-blue-500/20': log.operationType === 'UPDATE',
                  'bg-red-100 dark:bg-red-500/20': log.operationType === 'DELETE',
                  'bg-gray-100 dark:bg-gray-700': log.operationType !== 'CREATE' && log.operationType !== 'INSERT' && log.operationType !== 'UPDATE' && log.operationType !== 'DELETE'
                }">
                <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  [ngClass]="{
                    'text-green-600 dark:text-green-400': log.operationType === 'CREATE' || log.operationType === 'INSERT',
                    'text-blue-600 dark:text-blue-400': log.operationType === 'UPDATE',
                    'text-red-600 dark:text-red-400': log.operationType === 'DELETE',
                    'text-gray-500 dark:text-gray-400': log.operationType !== 'CREATE' && log.operationType !== 'INSERT' && log.operationType !== 'UPDATE' && log.operationType !== 'DELETE'
                  }">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="auditDisplay.entityIconPath(log.entityName)"/>
                </svg>
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0 pt-0.5">
                <!-- Clickable header -->
                <button class="w-full text-left" (click)="toggleLogDetail(log.id)">
                  <div class="flex items-center gap-2 flex-wrap">
                    <span class="rounded-full px-2 py-0.5 text-xs font-medium" [ngClass]="auditDisplay.operationBadgeClass(log.operationType)">
                      {{ auditDisplay.operationLabel(log) }}
                    </span>
                    <span class="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-500/10 dark:text-brand-400">
                      {{ auditDisplay.translateEntity(log.entityName) }}
                    </span>
                    @if (log.operationSubtype && auditDisplay.usesDescriptionHeadline(log)) {
                      <span class="text-xs text-gray-400" [title]="log.operationSubtype">{{ auditDisplay.subtypeLabel(log) }}</span>
                    }
                    @if (!log.success) {
                      <span class="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-500/10 dark:text-red-400">
                        Fallido
                      </span>
                    }
                    <!-- Expand indicator -->
                    @if (auditDisplay.hasDiff(log)) {
                      <svg class="h-3.5 w-3.5 text-gray-400 transition-transform" [class.rotate-180]="expandedLogId === log.id" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                      </svg>
                    }
                  </div>
                  <p class="mt-1 text-sm text-gray-700 dark:text-gray-300">
                    {{ auditDisplay.getTimelineHeadline(log) }}
                  </p>
                  <!-- Change summary preview -->
                  @if (auditDisplay.getChangeSummary(log); as summary) {
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {{ summary }}
                    </p>
                  }
                  <!-- Guardado que no modificó ningún campo (registros antiguos): se rotula
                       explícitamente para que no parezca una actualización a la que le faltan datos. -->
                  @if (auditDisplay.isFieldlessUpdate(log)) {
                    <p class="mt-1 text-xs italic text-gray-400 dark:text-gray-500">
                      Sin cambios de campos registrados
                    </p>
                  }
                  <div class="mt-1 flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500">
                    <span>{{ datetime.formatDateTimeNumeric(log.timestamp) }}</span>
                    @if (log.userFullName || log.username) {
                      <span>·</span>
                      <span>{{ log.userFullName ?? log.username }}</span>
                    }
                  </div>
                </button>

                <!-- Inc 25: enlace al detalle de la entidad relacionada (tarea/ticket) -->
                @if (isLinkable(log)) {
                  <button type="button"
                    (click)="onEntityLinkClick(log, $event)"
                    class="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400 dark:hover:text-brand-300">
                    <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                    </svg>
                    Ver detalle de {{ auditDisplay.translateEntity(log.entityName) }}
                  </button>
                }

                <!-- Changed fields chips -->
                @if (auditDisplay.getChangedFieldLabels(log).length > 0) {
                  <div class="mt-1.5 flex flex-wrap gap-1">
                    @for (field of auditDisplay.getChangedFieldLabels(log); track field) {
                      <span class="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                        {{ field }}
                      </span>
                    }
                  </div>
                }

                <!-- Expanded detail: field-by-field diff -->
                @if (expandedLogId === log.id && auditDisplay.hasDiff(log)) {
                  <div class="mt-3 rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50 overflow-hidden">
                    <table class="w-full text-xs">
                      <thead>
                        <tr class="border-b border-gray-200 dark:border-gray-700 text-left">
                          <th class="px-3 py-2 font-medium text-gray-500 dark:text-gray-400">Campo</th>
                          <th class="px-3 py-2 font-medium text-red-500 dark:text-red-400">Antes</th>
                          <th class="px-3 py-2 font-medium text-green-600 dark:text-green-400">Después</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                        @for (diff of auditDisplay.getDiffRows(log); track diff.field) {
                          <tr>
                            <td class="px-3 py-1.5 font-medium text-gray-700 dark:text-gray-300" [title]="diff.field">{{ diff.label }}</td>
                            <td class="px-3 py-1.5 text-gray-500 dark:text-gray-400 break-all"
                              [class.bg-red-50]="diff.oldVal !== diff.newVal"
                              [class.dark:bg-red-500/5]="diff.oldVal !== diff.newVal">
                              {{ diff.oldVal }}
                            </td>
                            <td class="px-3 py-1.5 text-gray-700 dark:text-gray-300 break-all"
                              [class.bg-green-50]="diff.oldVal !== diff.newVal"
                              [class.dark:bg-green-500/5]="diff.oldVal !== diff.newVal">
                              {{ diff.newVal }}
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                    @if (auditDisplay.getDiffRows(log).length === 0) {
                      <div class="px-3 py-3 text-xs text-gray-400 dark:text-gray-500 text-center">
                        No se pudieron extraer los cambios detallados.
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          }
        </div>
      </div>

      <!-- Pagination -->
      @if (totalPages > 1) {
        <div class="mt-4 flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-700">
          <p class="text-xs text-gray-500 dark:text-gray-400">
            Pág. {{ currentPage + 1 }} de {{ totalPages }}
            ({{ totalElements }})
          </p>
          <div class="flex gap-2">
            <button
              (click)="pageChange.emit(currentPage - 1)"
              [disabled]="currentPage === 0"
              class="rounded-lg border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors">
              Anterior
            </button>
            <button
              (click)="pageChange.emit(currentPage + 1)"
              [disabled]="currentPage + 1 >= totalPages"
              class="rounded-lg border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors">
              Siguiente
            </button>
          </div>
        </div>
      }
    }
  }
</div>
